import React from 'react';
import { CalendarEventResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { eventOccursOnDate } from '../utils/eventDate';
import { EventHoverCard } from './EventHoverCard';
interface MonthViewProps {
  currentDate: Date;
  events: CalendarEventResponse[];
  onEventClick: (event: CalendarEventResponse) => void;
  onDateClick: (date: Date) => void;
  onMoreEventsClick?: (date: Date) => void;
}

export const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onDateClick,
  onMoreEventsClick,
}) => {
  const getDaysInMonth = (): (Date | null)[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday

    // Start from the Sunday on/before the 1st so leading/trailing cells show
    // real dates from adjacent months (dimmed), matching the mockup grid.
    const gridStart = new Date(year, month, 1 - startingDayOfWeek);

    const totalCells = 42;
    const days: (Date | null)[] = [];
    for (let i = 0; i < totalCells; i++) {
      days.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
    }

    return days;
  };

  const getEventsForDate = (date: Date): CalendarEventResponse[] => {
    return events.filter(event => eventOccursOnDate(event, date));
  };

  const isToday = (date: Date | null): boolean => {
    if (!date) return false;

    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSameMonth = (date: Date | null): boolean => {
    if (!date) return false;
    return date.getMonth() === currentDate.getMonth();
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Map Google Calendar Color IDs (1-11) to hex colors
  const getEventColor = (colorId?: string, eventType?: string): string => {
    if (eventType === 'birthday') return '#10b981'; // Emerald-500 para aniversários
    if (eventType === 'activity') return '#f97316'; // Orange-500 para atividades

    const colors: Record<string, string> = {
      '1': '#a4bdfc',  // Lavender
      '2': '#7ae7bf',  // Sage
      '3': '#dbadff',  // Grape
      '4': '#ff887c',  // Flamingo
      '5': '#fbd75b',  // Banana
      '6': '#ffb878',  // Tangerine
      '7': '#46d6db',  // Peacock
      '8': '#e1e1e1',  // Graphite
      '9': '#5484ed',  // Blueberry
      '10': '#51b749', // Basil
      '11': '#dc2127', // Tomato
    };
    return colors[colorId || '9'] || colors['9']; // Default to Blueberry
  };

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const days = getDaysInMonth();

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--theme-card-background)' }}>
      {/* Week days header */}
      <div
        className="grid grid-cols-7 border-b flex-shrink-0"
        style={{ borderColor: 'var(--theme-border)' }}
      >
        {weekDays.map((day, index) => (
          <div
            key={index}
            className="py-1.5 px-2 text-right text-[11px] font-semibold uppercase"
            style={{
              color: 'var(--theme-muted-text)',
              borderLeft: index === 0 ? 'none' : '1px solid var(--theme-border)',
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days grid (6 weeks x 7 days) */}
      <div className="grid grid-cols-7 flex-1 min-h-0" style={{ gridTemplateRows: 'repeat(6, minmax(0, 1fr))' }}>
        {days.map((date, index) => {
          const dayEvents = date ? getEventsForDate(date) : [];
          const visibleEvents = dayEvents.slice(0, 3);
          const hiddenCount = dayEvents.length - visibleEvents.length;
          const today = isToday(date);
          const dim = date ? !isSameMonth(date) : false;

          return (
            <div
              key={index}
              className="p-1 min-h-0 overflow-hidden flex flex-col gap-0.5 cursor-pointer transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
              style={{
                borderLeft: index % 7 === 0 ? 'none' : '1px solid var(--theme-border)',
                borderBottom: '1px solid var(--theme-border)',
                backgroundColor: 'var(--theme-card-background)',
              }}
              onClick={() => date && onDateClick(date)}
            >
              {date && (
                <>
                  {/* Day number */}
                  <div
                    className="text-xs font-semibold self-start w-[22px] h-[22px] flex items-center justify-center rounded-full flex-shrink-0"
                    style={{
                      color: today ? '#FFFFFF' : dim ? 'var(--theme-muted-text)' : 'var(--theme-card-text)',
                      backgroundColor: today ? 'var(--theme-primary)' : 'transparent',
                      opacity: dim && !today ? 0.6 : 1,
                    }}
                  >
                    {date.getDate()}
                  </div>

                  {/* Events list */}
                  <div className="flex flex-col gap-0.5 min-h-0">
                    {visibleEvents.map((event) => {
                      const color = getEventColor(event.colorId, event.eventType);
                      const cancelled = event.status === 'cancelled';

                      return (
                        <EventHoverCard key={event.id} event={event}>
                          <div
                            className="flex items-center gap-1 h-[19px] px-1.5 rounded text-[11px] font-medium overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                            style={
                              event.allDay
                                ? { backgroundColor: color, color: '#FFFFFF' }
                                : { backgroundColor: 'transparent', color: 'var(--theme-card-text)' }
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick(event);
                            }}
                          >
                            {event.eventType === 'birthday' && (
                              <Icon name="box" className="w-3 h-3 flex-shrink-0" />
                            )}
                            {!event.allDay && (
                              <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: color }}
                              />
                            )}
                            {!event.allDay && (
                              <span
                                className="flex-shrink-0 tabular-nums"
                                style={{ color: 'var(--theme-muted-text)' }}
                              >
                                {formatTime(event.startTime)}
                              </span>
                            )}
                            <span
                              className="flex-1 min-w-0 truncate"
                              style={cancelled ? { textDecoration: 'line-through', opacity: 0.7 } : undefined}
                            >
                              {event.summary}
                            </span>
                          </div>
                        </EventHoverCard>
                      );
                    })}

                    {/* "More X events" indicator */}
                    {hiddenCount > 0 && (
                      <div
                        className="text-[11px] font-semibold px-1.5 cursor-pointer transition-colors text-[var(--theme-muted-text)] hover:text-[var(--theme-primary)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onMoreEventsClick && date) {
                            onMoreEventsClick(date);
                          }
                        }}
                      >
                        +{hiddenCount} mais
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
